import React, { useEffect, useState } from 'react'
import Topbar from '../../Components/Topbar'
import LeftbarUser from '../../Components/LeftbarUser'
import BottomNavUser from '../../Components/BottomNavUser'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSun, faMoon, faCloudSun } from '@fortawesome/free-solid-svg-icons'
import { app } from "../../firebase.js"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import {
  doc,
  getDoc,
  getFirestore,
  collection,
  query,
  where,
  getDocs
} from "firebase/firestore"
import { useNavigate } from 'react-router-dom'

import dashImage from '../../Images/dashone.svg'
import dashb from '../../Images/dashb1.png'

function UserDashboard() {
  const auth = getAuth()
  const db = getFirestore(app)
  const navigate = useNavigate()

  const [username, setUsername] = useState("Hey Stranger")
  const [userId, setUserId] = useState(null)

  const [ads, setAds] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [totalInfluencersWorkedWith, setTotalInfluencersWorkedWith] = useState(0)

  useEffect(() => {
    document.title = "Zindua App | User Dashboard";

    // Meta description
    let desc = document.querySelector("meta[name='description']");
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", "Zindua App user dashboard. Track your subscriptions, running ads, and influencers you work with.");

    // Open Graph tags
    const ogTags = [
      { property: "og:title", content: "Zindua App | User Dashboard" },
      { property: "og:description", content: "View your subscriptions, ads, and influencer collaborations on Zindua App." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://zindua.app/user/dashboard" },
      { property: "og:image", content: "https://zindua.app/logo192.png" },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property='${property}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    });

    // JSON-LD structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Zindua App User Dashboard",
      "url": "https://zindua.app/user/dashboard",
      "description": "Zindua App user dashboard for managing subscriptions, ads, and influencer collaborations.",
      "inLanguage": "en",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://zindua.app/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "User Dashboard",
            "item": "https://zindua.app/user/dashboard"
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);


  const [adsStats, setAdsStats] = useState({
    total: 0,
    processed: 0,
    rejected: 0
  })

  const [subsStats, setSubsStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    uniqueInfluencers: 0
  })

  /* ---------------- Greeting ---------------- */
  const hour = new Date().getHours()
  let greeting = "Good morning"
  let icon = faSun
  if (hour >= 12 && hour < 17) {
    greeting = "Good afternoon"
    icon = faCloudSun
  } else if (hour >= 17) {
    greeting = "Good evening"
    icon = faMoon
  }

  /* ---------------- Helpers ---------------- */
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false
    const now = new Date()
    const expiry = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate)
    return expiry < now
  }

  /* ---------------- Fetch User ---------------- */
  useEffect(() => {
    const cached = JSON.parse(localStorage.getItem("profileData") || "{}")
    if (cached.userName) setUsername(cached.userName)

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return

      setUserId(user.uid)

      const snap = await getDoc(doc(db, "users", user.uid))
      if (!snap.exists()) return

      const data = snap.data()
      const freshName = data.userName || ""

      if (freshName && freshName !== cached.userName) {
        setUsername(freshName)
        localStorage.setItem(
          "profileData",
          JSON.stringify({ ...cached, userName: freshName })
        )
      }
    })

    return () => unsubscribe()
  }, [auth, db])

  /* ---------------- Fetch Ads ---------------- */
  useEffect(() => {
    if (!userId) return

    const fetchAds = async () => {
      const adsRef = collection(db, 'ads')
      const q = query(adsRef, where('loggedInUserId', '==', userId))
      const snap = await getDocs(q)

      const data = snap.docs.map(doc => doc.data())
      setAds(data)

      setAdsStats({
        total: data.length,
        processed: data.filter(a => a.adStatus === 'Completed').length,
        rejected: data.filter(a => a.adStatus === 'Rejected').length
      })
    }

    fetchAds()
  }, [userId, db])

  /* ---------------- Fetch Subscriptions ---------------- */
  useEffect(() => {
    if (!userId) return

    const fetchSubs = async () => {
      const subRef = collection(db, 'subscriptions')
      const q = query(subRef, where('loggedInUserId', '==', userId))
      const snap = await getDocs(q)

      const data = snap.docs.map(doc => doc.data())
      setSubscriptions(data)

      const active = data.filter(s => !isExpired(s.expiryDate)).length
      const expired = data.filter(s => isExpired(s.expiryDate)).length

      const uniqueInfluencers = new Set(
        data.map(s => s.influencerId).filter(Boolean)
      ).size

      setSubsStats({
        total: data.length,
        active,
        expired,
        uniqueInfluencers
      })
    }

    fetchSubs()
  }, [userId, db])

  /* ---------------- This Month Ads ---------------- */
  const thisMonthAds = ads.filter(a => {
    const d = a.timestamp?.toDate?.()
    return d && d.getMonth() === new Date().getMonth()
  }).length

  //inf count

  useEffect(() => {
    if (!userId) return

    const adInfluencerIds = ads
      .map(a => a.influencerId)
      .filter(Boolean)

    const subscriptionInfluencerIds = subscriptions
      .map(s => s.influencerId)
      .filter(Boolean)

    const uniqueInfluencers = new Set([
      ...adInfluencerIds,
      ...subscriptionInfluencerIds
    ])

    setTotalInfluencersWorkedWith(uniqueInfluencers.size)
  }, [ads, subscriptions, userId])



  return (
    <div className="mainDashboard">
      <div className="desktopOnly">
        <LeftbarUser />
      </div>

      <div className="mainContent">
        <Topbar />

        <section className="userDash">
          {/* TOP */}
          <div className="userDashOne">
            <div className="userDashOneWelcome">
              <FontAwesomeIcon icon={icon} className="greetIcon" />
              <h3>{greeting}</h3>
              <h6>{username}</h6>
              <img src={dashImage} alt="" />
            </div>

            <div
              className="userDashOneTwo"
              onClick={() => navigate('/user/ads')}
              style={{ cursor: 'pointer' }}
            >
              <div className='userDashOneTwoTop'>
                <div>
                  <h6>Influencers</h6>
                  <p>Total creators worked with</p>
                </div>
              </div>
              <h4>{totalInfluencersWorkedWith}</h4>
              <img src={dashb} alt="" />
            </div>
          </div>

          {/* STATS */}
          <div className="userDashTwo">

            {/* ADS */}
            <div className="dashBox box1color">
              <p className='box1colorTitle'>Ads</p>
              <div className='dashBoxRoundCont'>
                <div className='dashBoxRound'>
                  <h3>{adsStats.total}</h3>
                  <p>Total Ads</p>
                </div>
              </div>
              <div className='box1colorBottom'>
                <p>Processed {adsStats.processed}</p>
                <p>Rejected {adsStats.rejected}</p>
              </div>
            </div>

            {/* SUBSCRIPTIONS */}
            <div className="dashBox box2color">
              <p className='box1colorTitle'>Subscriptions</p>
              <div className='dashBoxRoundCont'>
                <div className='dashBoxRound'>
                  <h3>{subsStats.total}</h3>
                  <p>Subscriptions</p>
                </div>
              </div>
              <div className='box1colorBottom'>
                <p>Active {subsStats.active}</p>
                <p>Expired {subsStats.expired}</p>
              </div>
            </div>

            {/* REPORTING */}
            <div className="dashBox box3color">
              <p className='box1colorTitle'>Reportings</p>
              <div className='dashBoxRoundCont'>
                <div className='dashBoxRound'>
                  <h3>{subsStats.active}</h3>
                  <p>Running Ads</p>
                </div>
              </div>
              <div className='box1colorBottom'>
                <p>Running Ads {subsStats.active}</p>
                <p>This Month's Ads {thisMonthAds}</p>
              </div>
            </div>

          </div>
        </section>
      </div>

      <div className="mobileOnly">
        <BottomNavUser />
      </div>
    </div>
  )
}

export default UserDashboard